/**
 * Types + pure helpers for the alignment artefacts.
 *
 * Kept free of `node:fs` so client components (the heatmap, bloc panel,
 * timeline) can import these helpers without dragging server-only
 * modules into the browser bundle. The actual file loader lives in
 * `./alignment.ts` and stays server-side.
 */

import type { ArtefactMeta } from "@/lib/artefact";

export type VoterRole =
  | "mayor"
  | "councillor"
  | "commissioner"
  | "public-member"
  | "other";

export type Voter = {
  id: number;
  name: string;
  role: VoterRole;
  ward_num: number | null;
  display_name: string;
};

export type Pair = {
  a_id: number;
  b_id: number;
  co_voted_count: number;
  agreement_count: number;
  agreement_rate: number | null;
  a_recused_b_voted: number;
  b_recused_a_voted: number;
  both_recused: number;
};

export type MatrixScope = {
  kind: "council" | "committee";
  slug?: string;
  label: string;
  category_names: string[];
  term_start: string;
  term_end: string;
};

export type MatrixStats = {
  n_meetings: number;
  n_motions: number;
  n_contested_motions: number;
  vote_sources_included: string[];
  recusal_handling: "third_direction";
};

export type MatrixView = {
  scope: MatrixScope;
  voters: Voter[];
  stats: MatrixStats;
  pairs: Pair[];
};

export type AlignmentMatrix = {
  meta: ArtefactMeta;
  data: MatrixView;
};

export type MotionIntent =
  | "advance"
  | "amend"
  | "undo"
  | "procedural"
  | "other";

export type VoterEntry = {
  id: number;
  name: string;
  source: string;
};

export type ContestedMotion = {
  motion_id: string;
  meeting_uid: string;
  meeting_slug: string | null;
  meeting_minutes_url: string;
  meeting_date: string;
  category_name: string;
  meeting_type: string;
  title: string;
  description: string;
  intent: MotionIntent;
  is_carried: boolean;
  bylaw_numbers?: string[];
  loc_codes?: string[];
  ward?: number | null;
  vote_summary: {
    for_count: number;
    against_count: number;
    recused_count: number;
    abstain_count: number;
  };
  voters: {
    for: VoterEntry[];
    against: VoterEntry[];
    recused: VoterEntry[];
    abstain: VoterEntry[];
  };
};

export type ContestedView = {
  term_start: string;
  term_end: string;
  stats: {
    n_motions: number;
    n_meetings: number;
    vote_sources_included: string[];
  };
  motions: ContestedMotion[];
};

export type ContestedMotions = {
  meta: ArtefactMeta;
  data: ContestedView;
};

export type BlocCluster = {
  label: string;
  size: number;
  member_ids: number[];
};

export type BlocsView = {
  scope: {
    kind: "council";
    label: string;
    term_start: string;
    term_end: string;
  };
  ordered_ids: number[];
  linkage: Array<{
    step: number;
    left: number | string;
    right: number | string;
    distance: number;
    merged_id?: string;
  }>;
  cuts: {
    k2: BlocCluster[];
    k3: BlocCluster[];
    k4: BlocCluster[];
  };
};

export type Blocs = {
  meta: ArtefactMeta;
  data: BlocsView;
};

export type AlignmentBundle = {
  council: AlignmentMatrix | null;
  committees: AlignmentMatrix[];
  contested: ContestedMotions | null;
  blocs: Blocs | null;
};

/**
 * Index `voters` by id for O(1) lookup at render time.
 */
export function voterMap(matrix: MatrixView): Map<number, Voter> {
  return new Map(matrix.voters.map((v) => [v.id, v]));
}

/**
 * Index pairs by an unordered "a|b" key so cell lookup works regardless
 * of which voter is on row vs. column. Returns null for missing pairs
 * (voters who never overlapped on a for/against vote).
 */
export function pairMap(matrix: MatrixView): Map<string, Pair> {
  const out = new Map<string, Pair>();
  for (const p of matrix.pairs) {
    const key =
      p.a_id < p.b_id ? `${p.a_id}|${p.b_id}` : `${p.b_id}|${p.a_id}`;
    out.set(key, p);
  }
  return out;
}

export function pairKey(a: number, b: number): string {
  return a < b ? `${a}|${b}` : `${b}|${a}`;
}

/**
 * Display order for the matrix:
 *   1. blocs.ordered_ids when available (complete-linkage hierarchical),
 *   2. fall back to ward number ascending (mayor first, then W1..W14),
 *   3. fall back to voter id when nothing else discriminates.
 *
 * Voters not present in `ordered_ids` are appended in fallback order so
 * a freshly-elected councillor with no data yet doesn't disappear.
 */
export function displayOrder(
  matrix: MatrixView,
  blocs: BlocsView | null,
): Voter[] {
  const voters = matrix.voters;
  const fallback = [...voters].sort((a, b) => {
    if (a.role === "mayor" && b.role !== "mayor") return -1;
    if (b.role === "mayor" && a.role !== "mayor") return 1;
    const aw = a.ward_num ?? 99;
    const bw = b.ward_num ?? 99;
    if (aw !== bw) return aw - bw;
    return a.id - b.id;
  });
  if (!blocs || matrix.scope.kind !== "council") return fallback;
  const ordered: Voter[] = [];
  const seen = new Set<number>();
  const byId = voterMap(matrix);
  for (const id of blocs.ordered_ids) {
    const v = byId.get(id);
    if (v) {
      ordered.push(v);
      seen.add(id);
    }
  }
  for (const v of fallback) {
    if (!seen.has(v.id)) ordered.push(v);
  }
  return ordered;
}

/**
 * Map voter id → bloc label (A/B/C/D) for a chosen cut. Returns an
 * empty map when blocs.json is absent.
 */
export function blocAssignments(
  blocs: BlocsView | null,
  k: 2 | 3 | 4,
): Map<number, string> {
  const out = new Map<number, string>();
  if (!blocs) return out;
  const clusters = k === 2 ? blocs.cuts.k2 : k === 3 ? blocs.cuts.k3 : blocs.cuts.k4;
  for (const c of clusters) {
    for (const id of c.member_ids) out.set(id, c.label);
  }
  return out;
}
